﻿// src/App.tsx - Version sécurisée sans SecurityHeader
import React, { useMemo, useEffect } from 'react';
import { Loader2, Shield, AlertTriangle } from 'lucide-react';
import ChatInterface from './components/ChatInterface';
import LoginPage from './components/LoginPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SecuredChatService } from './services/chatService';
import { ChatServiceConfig } from './types';
import './index.css';

console.log('[App] Secured application loaded');

// Configuration par défaut pour le service de chat sécurisé
const chatServiceConfig: ChatServiceConfig = {
  fetchUrl: 'https://sms-back.cigarette-electronique-tournefeuille.fr/webhook/get-sms-list',
  markAsReadUrl: 'https://sms-back.cigarette-electronique-tournefeuille.fr/webhook/conversation-opened',
  autoAIUrl: 'https://sms-back.cigarette-electronique-tournefeuille.fr/webhook/toggle-auto-ai',
  sendSMSUrl: 'https://sms-back.cigarette-electronique-tournefeuille.fr/webhook/send-manual-sms',
  apiTimeout: 15000, // Augmenté pour les requêtes sécurisées
  refreshInterval: 30000,
};

// Composant d'erreur pour les cas de problème de sécurité
const SecurityError: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="min-h-screen bg-red-900 flex items-center justify-center p-4">
    <div className="text-center max-w-md">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-full mb-4">
        <AlertTriangle size={24} className="text-white" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-4">Erreur de sécurité</h1>
      <p className="text-red-200 mb-6">{message}</p>
      <button
        onClick={onRetry}
        className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
      >
        Réessayer
      </button>
    </div>
  </div>
);

// Composant principal de l'application sécurisée
const SecuredAppContent: React.FC = () => {
  const { isAuthenticated, password, isLoading, logout } = useAuth();

  // Créer le service de chat sécurisé avec le mot de passe
  const securedChatService = useMemo(() => {
    if (password) {
      console.log('[App] Creating secured chat service with authentication');
      try {
        return new SecuredChatService(chatServiceConfig, password);
      } catch (error) {
        console.error('[App] Failed to create secured chat service:', error);
        return null;
      }
    }
    return null;
  }, [password]);

  // Mise à jour dynamique du mot de passe du service
  useEffect(() => {
    if (securedChatService && password) {
      securedChatService.setPassword(password);
    }
  }, [securedChatService, password]);

  // Écran de chargement initial
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-600 rounded-full mb-4">
            <Shield size={24} className="text-white" />
          </div>
          <Loader2 size={32} className="animate-spin text-slate-400 mb-4 mx-auto" />
          <p className="text-slate-300 font-medium">Vérification de l'authentification...</p>
          <p className="text-xs text-slate-500 mt-1">Oclope.fr - Système sécurisé</p>
        </div>
      </div>
    );
  }

  // Si pas authentifié, afficher la page de login
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Si authentifié mais service non créé, erreur de sécurité
  if (!securedChatService) {
    return (
      <SecurityError 
        message="Impossible d'initialiser le service sécurisé. Veuillez vous reconnecter."
        onRetry={logout}
      />
    );
  }

  // Si authentifié, afficher l'interface de chat sécurisée
  return (
    <div className="secured-app h-screen">
      <ChatInterface chatService={securedChatService} />
    </div>
  );
};

// Composant racine avec le provider d'authentification
const App: React.FC = () => {
  return (
    <AuthProvider>
      <div className="App">
        <SecuredAppContent />
      </div>
    </AuthProvider>
  );
};

export default App;